import { middlewareGateway } from '~/gateway/middleware.gateway'

/**
 * A composable function to fetch stock information for given stores and SKUs.
 *
 * @returns {Object} An object containing the `getStoreStock` function, `result`, `loading`, and `error` states.
 *
 * @example
 * const { getStoreStock, result, loading, error } = useStoreStock();
 * const stockData = await getStoreStock(['416', '428'], ['15466303', '15465931']);
 * console.log(result.value, loading.value, error.value);
 *
 * @function
 * @name useStoreStock
 */
export default function useStoreStock() {
  const result = ref<StockResult>({ stores: [] })
  const loading = ref(false)
  const error = ref()

  // BOPIS: ask the middleware for DC stock enrichment (adds `source` per stock
  // line). Captured at setup so getStoreStock stays callable outside setup.
  const { bopisEnabled } = useFeatureFlags()
  const locale = useNuxtApp().$i18n.locale.value
  const includeWarehouse = bopisEnabled() ? (localeConfig[locale]?.country ?? '') : ''

  /**
   * Fetches stock information for the specified stores and SKUs.
   *
   * @param {string[]} stores - An array of store identifiers.
   * @param {string[]} skus - An array of SKU identifiers.
   * @returns {Promise<StockResult | undefined>} The fetched stock, or undefined when the lookup
   * fails. Prefer this over `result`, which retains the previous response on failure.
   *
   * @example
   * await getStoreStock(['416', '428'], ['15466303', '15465931']);
   * console.log(result.value);
   *
   * @remarks
   * This function sets the `loading` state to `true` while fetching the data and sets it back to `false` once the operation is complete.
   * If an error occurs during the fetch operation, it is caught and assigned to the `error` state, and logged to the console.
   * If the fetch operation is successful, the resulting data is assigned to the `result` state.
   */
  async function getStoreStock(stores: string[], skus: string[]) {
    try {
      error.value = undefined
      if (!stores?.length || !skus?.length) return
      loading.value = true
      const warehouseParam = includeWarehouse ? `&includeWarehouse=${includeWarehouse}` : ''
      const queryParams = `store=${stores.join(',')}&sku=${skus.join(',')}${warehouseParam}`
      const data = await middlewareGateway<StockResult>(`/next-gen/mhj/stock?${queryParams}`)
      if (!data) return
      result.value = data
      return data
    } catch (e) {
      error.value = e
    } finally {
      loading.value = false
    }
  }

  return {
    getStoreStock,
    result: computed(() => result.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
  }
}
